import { AppBar, Box, Tab, Tabs, Toolbar } from '@mui/material';
import { BrandKey } from '@prismamedia/one-brandkey';
import {
  AutocompleteItem,
  BrandMenu,
  BrandMenuItem,
  InfiniteList,
  SearchBar,
  SearchBarVariant,
  useOnMount,
} from '@prismamedia/one-components';
import { RecipeOrderByInput } from '__generated__/queries-recipe';
import { ArticleFormat, ArticleStatus } from '__generated__/queries-web';
import { useGetRecipesPromise } from 'apollo/queries/recipes.recipe.graphql';
import moment from 'moment';
import { parse } from 'query-string';
import {
  ChangeEvent,
  Dispatch,
  FC,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { extractPath } from 'utils/extractPath';
import {
  BLOCK_ITEM_HEIGHT,
  BLOCK_ITEM_MARGIN,
} from '../Items/desktop/BlockItem/styles';
import { BlockItemType } from '../Items/utils';
import { actions, CampaignSectionAction } from '../reducer';
import { useDisplayArticles } from '../utils/useDisplayArticles';
import {
  PAGINATION,
  resources,
  ResourceType,
  SearchParams,
} from '../utils/useGetBlockItems';
import { AdvancedSearch } from './AdvancedSearch';
import { RecipeAdvancedSearch } from './AdvancedSearch/Recipe';
import { BlockItem } from './BlockItem';
import { useStyles } from './styles';

interface ResourceListProps {
  articleFormat: ArticleFormat | undefined;
  blockItems?: BlockItemType[];
  brandKey: BrandKey | null | undefined;
  dispatch: Dispatch<CampaignSectionAction>;
  error?: Error;
  fetchMore: (variables: any) => Promise<any>;
  loading: boolean;
  onTabSelect: (tabId: string) => void;
  resourceType: ResourceType;
  searchParams: SearchParams;
  sectionsIds: string[];
  setResourceType: (resourceType: ResourceType) => void;
  setSearchParams: (searchParams: SearchParams) => void;
}

const initialArticleTabs = [
  {
    id: ArticleStatus.Published,
    label: 'Publiés',
    selected: true,
    disabled: false,
  },
  {
    id: ArticleStatus.Scheduled,
    label: 'Programmés',
    selected: false,
    disabled: false,
  },
];

export const ResourceList: FC<ResourceListProps> = ({
  articleFormat,
  blockItems,
  brandKey,
  dispatch,
  error,
  fetchMore,
  loading,
  onTabSelect,
  resourceType,
  searchParams,
  sectionsIds,
  setResourceType,
  setSearchParams,
}) => {
  const advancedSearch = useMemo(() => {
    if (resourceType === ResourceType.RECIPE) {
      return {
        component: RecipeAdvancedSearch,
        props: { brandKey },
      };
    } else if (resourceType === ResourceType.ARTICLE) {
      return {
        component: AdvancedSearch,
        props: { articleFormat, brandKey },
      };
    } else if (resourceType === ResourceType.DIAPO) {
      return {
        component: AdvancedSearch,
        props: { articleFormat, brandKey },
      };
    }

    return undefined;
  }, [articleFormat, brandKey, resourceType]);

  const classes = useStyles();
  const location = useLocation();
  const [articlesTabs, setArticlesTab] = useState(initialArticleTabs);
  const { isMobile } = useDisplayArticles();
  const getRecipes = useGetRecipesPromise();

  const resource = resources.find(({ type }) => type === resourceType);

  const fetchAutocompleteList = async (
    search: string,
  ): Promise<AutocompleteItem[] | undefined> => {
    const searchTitlePath = extractPath(search);

    const recipes = await getRecipes({
      first: 5,
      orderBy: [RecipeOrderByInput.publishedAt_DESC],
      where: {
        brandKey,
        title_contains: searchTitlePath ? undefined : search || undefined,
        recipeUrls_some: searchTitlePath
          ? { url: { path: searchTitlePath } }
          : undefined,
      },
    });

    return recipes?.map(({ id, title, publishedAt }) => ({
      id,
      label: title,
      secondaryLabel: publishedAt
        ? moment(publishedAt).format('D MMM')
        : undefined,
    }));
  };

  const handleAddArticle = useCallback(
    (sectionId: string, blockType: string, articleData: BlockItemType) => {
      dispatch(actions.addBlock({ sectionId, blockType, articleData }));
    },
    [dispatch],
  );

  const handleArticleTypeSelect = ({ id }: BrandMenuItem) => {
    setSearchParams({ search: searchParams.search });
    setResourceType(id as ResourceType);
  };

  const handleTabSelect = useCallback(
    (_: ChangeEvent<any>, indexSelected: number) => {
      setArticlesTab((currArticleTabs) =>
        currArticleTabs.map((tab, index) => ({
          ...tab,
          selected: index === indexSelected,
        })),
      );

      onTabSelect(articlesTabs[indexSelected].id);
    },
    [articlesTabs, onTabSelect],
  );

  useOnMount(() => {
    const queryParams = parse(location.search);

    if (queryParams.status) {
      setArticlesTab((currArticleTabs) =>
        currArticleTabs.map((tab) => ({
          ...tab,
          selected: tab.id === queryParams.status,
        })),
      );
    }
  });

  return (
    <Box display="flex" flexDirection="column" className={classes.root}>
      <Toolbar className={classes.toolBar}>
        <BrandMenu
          dark
          items={resources
            .filter(
              ({ allowedBrands }) =>
                brandKey &&
                (!allowedBrands || allowedBrands.includes(brandKey)),
            )
            .map(({ label, type }) => ({
              label,
              id: type,
              selected: type === resourceType,
            }))}
          onItemSelect={handleArticleTypeSelect}
        />
        <SearchBar
          advancedSearch={advancedSearch}
          autocomplete={{
            fetchList:
              resourceType === ResourceType.RECIPE
                ? fetchAutocompleteList
                : undefined,
            onSelect: ({ label }) => {
              setSearchParams({
                search: typeof label === 'string' ? label : '',
              });
            },
          }}
          className={classes.searchBar}
          placeholder={resource?.placeholder}
          setSearchParams={setSearchParams}
          searchParams={searchParams}
          variant={SearchBarVariant.TERTIARY}
        />
      </Toolbar>

      <Box>
        <AppBar
          position="relative"
          color="default"
          className={classes.tabsAppBar}
        >
          <Tabs
            centered
            indicatorColor="primary"
            onChange={handleTabSelect}
            textColor="primary"
            value={articlesTabs.findIndex(({ selected }) => selected)}
          >
            {articlesTabs.map((tab) => (
              <Tab disabled={tab.disabled} key={tab.id} label={tab.label} />
            ))}
          </Tabs>
        </AppBar>

        <div className={classes.articleContainer}>
          <InfiniteList
            className={classes.list}
            draggable={{
              droppableId: 'article',
              type: 'article',
              isDropDisabled: isMobile,
              isDragDisabled: isMobile,
            }}
            error={error}
            fetchMore={({ startIndex }) =>
              fetchMore({ variables: { skip: startIndex } })
            }
            list={blockItems}
            loading={loading}
            rowHeight={BLOCK_ITEM_HEIGHT + BLOCK_ITEM_MARGIN + 4}
            rowRenderer={(blockItem: BlockItemType) => (
              <BlockItem
                blockItem={blockItem}
                isMobile={isMobile}
                onAddArticle={handleAddArticle}
                resource={resource}
                sectionsIds={sectionsIds}
              />
            )}
            threshold={PAGINATION / 2}
          />
        </div>
      </Box>
    </Box>
  );
};
